// src/components/dashboard/UnitEdit/UnitTenant/UnitTenant.tsx
import React from 'react';
import { Formik, Form } from 'formik';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import Input from '@/components/shared/Input';
import Button from '@/components/shared/Button';
import './UnitTenant.scss';

export interface TenantInfo {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  avatar?: string | null; //  | null
}

export interface InviteInfo {
  id?: number;
  token: string;
  email?: string | null; //  | null
  createdAt?: Date;
  isUsed?: boolean;
}

export interface UnitTenantData {
  tenant: TenantInfo | null;
  invite: InviteInfo | null;
}

export interface CreateInviteData {
  email: string;
  monthlyRent: number;
}

export interface UnitTenantProps {
  /**
   * Дані секції
   */
  data: UnitTenantData;

  /**
   * Callback для створення pending lease
   */
  onCreateInvite?: (data: CreateInviteData) => Promise<void>;

  /**
   * Callback для скасування pending lease
   */
  onCancelInvite?: () => Promise<void>;

  /**
   * Callback для завершення lease
   */
  onTerminateLease?: () => Promise<void>;

  /**
   * Стан завантаження
   */
  loading?: boolean;

  /**
   * Контрольований стан згорнутості
   */
  collapsed?: boolean;

  /**
   * Обробник зміни стану згорнутості
   */
  onToggleCollapse?: (collapsed: boolean) => void;

  /**
   * Додатковий CSS клас
   */
  className?: string;
}

const baseClass = 'UnitTenant';

/**
 * Компонент для відображення та управління орендарем юніта
 */
const UnitTenant: React.FC<UnitTenantProps> = ({
  data,
  onCreateInvite,
  onCancelInvite,
  onTerminateLease,
  loading = false,
  collapsed,
  onToggleCollapse,
  className = '',
}) => {
  const { tenant, invite } = data;

  const handleCreateInvite = async (values: CreateInviteData) => {
    if (onCreateInvite) {
      await onCreateInvite(values);
    }
  };

  const handleCancelInvite = async () => {
    if (onCancelInvite) {
      await onCancelInvite();
    }
  };

  const handleTerminateLease = async () => {
    if (onTerminateLease) {
      await onTerminateLease();
    }
  };

  const renderContent = () => {
    // Якщо є активний tenant
    if (tenant) {
      return (
        <div className={`${baseClass}__info`}>
          <div className={`${baseClass}__info-row`}>
            <span className={`${baseClass}__info-label`}>Name:</span>
            <span className={`${baseClass}__info-value`}>
              {tenant.firstname} {tenant.lastname}
            </span>
          </div>
          <div className={`${baseClass}__info-row`}>
            <span className={`${baseClass}__info-label`}>Email:</span>
            <span className={`${baseClass}__info-value`}>{tenant.email}</span>
          </div>
          {tenant.phone && (
            <div className={`${baseClass}__info-row`}>
              <span className={`${baseClass}__info-label`}>Phone:</span>
              <span className={`${baseClass}__info-value`}>{tenant.phone}</span>
            </div>
          )}
          <div className={`${baseClass}__actions`}>
            <Button type="button" theme="danger" onClick={handleTerminateLease}>
              Terminate lease
            </Button>
          </div>
        </div>
      );
    }

    // Якщо є pending invite
    if (invite) {
      return (
        <div className={`${baseClass}__invite`}>
          <div className={`${baseClass}__info-row`}>
            <span className={`${baseClass}__info-label`}>Pending invite:</span>
            <span className={`${baseClass}__info-value`}>{invite.email}</span>
          </div>
          <div className={`${baseClass}__info-row`}>
            <span className={`${baseClass}__info-label`}>Sent:</span>
            <span className={`${baseClass}__info-value`}>
    {invite.createdAt ? new Date(invite.createdAt).toLocaleDateString() : 'N/A'}
  </span>
          </div>
          <div className={`${baseClass}__actions`}>
            <Button type="button" theme="danger" onClick={handleCancelInvite}>
              Cancel invite
            </Button>
          </div>
        </div>
      );
    }

    // Якщо немає ні tenant, ні invite - показуємо форму
    return (
      <Formik
        initialValues={{
          email: '',
          monthlyRent: 0,
        }}
        onSubmit={handleCreateInvite}
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form className={`${baseClass}__form`}>
            <div className={`${baseClass}__form-input`}>
              <Input
                onChange={handleChange}
                onBlur={handleBlur}
                name="email"
                type="email"
                value={values.email}
                placeholder="Tenant email"
                errors={errors.email && touched.email ? errors.email : ''}
              />
            </div>
            <div className={`${baseClass}__form-input`}>
              <Input
                onChange={handleChange}
                onBlur={handleBlur}
                name="monthlyRent"
                type="number"
                value={values.monthlyRent}
                placeholder="Monthly rent"
                errors={
                  errors.monthlyRent && touched.monthlyRent
                    ? errors.monthlyRent
                    : ''
                }
              />
            </div>
            <div className={`${baseClass}__actions`}>
              <Button type="submit" theme="primary">
                Send invite
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    );
  };

  return (
    <UICollapsibleSection
      title="Tenant"
      description=""
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      loading={loading}
      loadingText="Loading tenant information..."
      className={`${baseClass} ${className}`.trim()}
    >
      {renderContent()}
    </UICollapsibleSection>
  );
};

export default UnitTenant;
